import { colors } from '@ardoq/design-tokens';
import {
  INode,
  IRenderContext,
  MatrixOrder,
  NodeStyleBase,
  Point,
  SvgVisual,
  Visual,
} from '@ardoq/yfiles';
import { createSvgElement } from '@ardoq/dom-utils';
import { GraphItem } from '@ardoq/graph';
import { CONTEXT_HIGHLIGHT_PADDING } from 'yfilesExtensions/styles/consts';
import {
  isContextNode,
  isImageNode,
} from 'yfilesExtensions/styles/nodeDecorator';
import './DragAndDropImageHighlightIndicatorManager.css';
import { IsValidDropTargetCallback } from '../modernized/types';

const MARGIN = 1;
const GROUP_NODE_CORNER_RADIUS = 4;

interface RenderDataCache {
  shape: 'circle' | 'rect';
  padding: number;
  width: number;
  height: number;
  isGroupNode: boolean;
  isHovered: boolean;
  zoomedIn: boolean;
  isValidDropTarget: boolean;
  equals: (self: RenderDataCache, other: RenderDataCache) => boolean;
}

const renderDataCache = new WeakMap<Element, RenderDataCache>();

/**
 * Creates an object containing all necessary data to create a visual for the node.
 */
const createRenderDataCache = (
  node: INode,
  context: IRenderContext,
  isValidDropTargetCallback: IsValidDropTargetCallback | null
): RenderDataCache => {
  const padding = isContextNode(node) ? CONTEXT_HIGHLIGHT_PADDING : 0;
  const isGroupNode = node.tag.isGroupNode;

  const isValidDropTarget = isValidDropTargetCallback
    ? isValidDropTargetCallback(node)
    : false;

  return {
    shape: isImageNode(node) && !isGroupNode ? 'circle' : 'rect',
    padding,
    width: node.layout.width - 2 * padding,
    height: node.layout.height - 2 * padding,
    isGroupNode,
    isHovered: node.tag.isHovered,
    zoomedIn: context.zoom > 1,
    isValidDropTarget,
    equals: (self, other): boolean =>
      self.shape === other.shape &&
      self.padding === other.padding &&
      self.width === other.width &&
      self.height === other.height &&
      self.isHovered === other.isHovered &&
      self.isGroupNode === other.isGroupNode &&
      self.zoomedIn === other.zoomedIn &&
      self.isValidDropTarget === other.isValidDropTarget,
  };
};
const render = (container: SVGElement, cache: RenderDataCache) => {
  // store information with the visual on how we created it
  renderDataCache.set(container, cache);

  if (cache.isValidDropTarget) {
    const animatedOutline = createSvgElement(cache.shape, {
      class: 'drag-and-drop-highlight-animated-outline',
      width: `${cache.width}`,
      height: `${cache.height}`,
      cy: `${cache.height / 2}`,
      cx: `${cache.width / 2}`,
      r: `${cache.height / 2}`,
      rx: cache.isGroupNode ? `${GROUP_NODE_CORNER_RADIUS}` : '0',
      ry: cache.isGroupNode ? `${GROUP_NODE_CORNER_RADIUS}` : '0',
      fill: 'transparent',
      stroke: cache.isHovered ? colors.blue700 : colors.blue600,
      ['stroke-linecap']: 'square',
      ['stroke-width']: '4',
      ['stroke-dasharray']: cache.isHovered
        ? '0'
        : cache.shape === 'circle'
          ? '12.8'
          : '16',
      style: 'pointer-events: none',
    });

    container.append(animatedOutline);
  }
};

const maybeRenderInvalidDropzoneClipPath = (
  context: IRenderContext,
  node: INode,
  cache: RenderDataCache
) => {
  if (!cache.isValidDropTarget) {
    const id = (node.tag as GraphItem).id;

    const invalidMask = createSvgElement('rect', {
      'data-id': id,
      width: `${cache.width - 2 * MARGIN}`,
      height: `${cache.height - 2 * MARGIN}`,
      rx: cache.isGroupNode ? `${GROUP_NODE_CORNER_RADIUS}` : '0',
      ry: cache.isGroupNode ? `${GROUP_NODE_CORNER_RADIUS}` : '0',
      x: `${MARGIN}`,
      y: `${MARGIN}`,
      style: 'pointer-events: none',
    });

    const { padding } = cache;
    const matrix = context.viewTransform.clone();
    matrix.invert();
    matrix.translate(new Point(padding, padding), MatrixOrder.PREPEND);
    matrix.translate(node.layout.topLeft, MatrixOrder.PREPEND);
    matrix.applyTo(invalidMask);

    const clipNode =
      context.canvasComponent?.overlayPanel.querySelector('#clip');

    if (clipNode?.hasChildNodes()) {
      for (const child of clipNode.childNodes) {
        if ((child as SVGRectElement).dataset.id === id) {
          clipNode.removeChild(child);
        }
      }
    }

    clipNode?.appendChild(invalidMask);
  }
};

class DropzoneNodeStyle extends NodeStyleBase {
  static MARGIN = MARGIN;

  constructor(
    private readonly isValidDropTargetCallback: IsValidDropTargetCallback | null = null
  ) {
    super();
  }

  /**
   * Creates the visual for a node.
   * @see Overrides {@link NodeStyleBase.createVisual}
   */
  override createVisual(context: IRenderContext, node: INode): Visual | null {
    const g = createSvgElement('g');

    const cache = createRenderDataCache(
      node,
      context,
      this.isValidDropTargetCallback
    );
    const { padding } = cache;

    render(g, cache);
    maybeRenderInvalidDropzoneClipPath(context, node, cache);

    SvgVisual.setTranslate(g, node.layout.x + padding, node.layout.y + padding);

    return new SvgVisual(g);
  }

  /**
   * Re-renders the node using the old visual for performance reasons.
   * @see Overrides {@link NodeStyleBase.updateVisual}
   */
  override updateVisual(
    context: IRenderContext,
    oldVisual: SvgVisual,
    node: INode
  ): SvgVisual {
    const g = oldVisual.svgElement;
    const oldCache = renderDataCache.get(g);
    const newCache = createRenderDataCache(
      node,
      context,
      this.isValidDropTargetCallback
    );
    const { padding } = newCache;

    if (!oldCache || !newCache.equals(newCache, oldCache)) {
      // something changed - re-render the visual
      while (g.hasChildNodes()) {
        g.removeChild(g.firstChild!);
      }
      render(g, newCache);
    }
    maybeRenderInvalidDropzoneClipPath(context, node, newCache);

    SvgVisual.setTranslate(g, node.layout.x + padding, node.layout.y + padding);

    return oldVisual;
  }
}

export { DropzoneNodeStyle };
